/* eslint-disable @ngrx/avoid-dispatching-multiple-actions-sequentially */
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { WeakPasswordDialogComponent } from '../weak-password-dialog/weak-password-dialog.component';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideAlertCircle, lucideCheck, lucideEye, lucideEyeOff } from '@ng-icons/lucide';
import { HlmIcon } from '../../../ui/icon/src';
import { HlmInput } from '../../../ui/input/src';
import { HlmButton } from '../../../ui/button/src';
import { HlmLabel } from '../../../ui/label/src';
import { ActivatedRoute, Router } from '@angular/router';
import { Errors } from '@core/models/errors';
import { LoadingService } from '@core/services/loading.service';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { EMPTY, Subscription } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { toast } from 'ngx-sonner';
import { errorHandlingFunction } from 'src/app/core/models/error-handling';
import { AuthService } from '../../services/auth.service';
import ResetPasswordRequestErrorMessages = Errors.ResetPasswordRequestErrorMessages;

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgIcon,
    HlmIcon,
    HlmInput,
    HlmLabel,
    HlmButton
  ],
  providers: [provideIcons({ lucideEye, lucideEyeOff, lucideAlertCircle, lucideCheck })],
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private dialog = inject(HlmDialogService);
  private route = inject(ActivatedRoute);
  private authSrv = inject(AuthService);
  private fb = inject(UntypedFormBuilder);
  private loadingSrv = inject(LoadingService);

  resetPasswordForm!: UntypedFormGroup;

  visiblePwd = false;
  email: string | null = null;
  resetToken: string | null = null;



  private sub = new Subscription();

  ngOnInit(): void {
    const queryParamsSub = this.route.queryParams
      .pipe(
        tap(params => {
          if (params['email'] && params['token']) {
            // restheart-accounts: token from email link
            this.createResetPasswordForm(params['email'], params['token']);
          } else if (params['email'] && params['otp']) {
            // legacy fallback for old links during transition
            this.createResetPasswordForm(params['email'], params['otp']);
          } else {
            this.router.navigate(['', 'auth', 'login']);
          }
        })
      )
      .subscribe();

    this.sub.add(queryParamsSub);
  }

  get hasPasswordMissmatchError(): boolean {
    return this.resetPasswordForm.hasError('passwordMismatch');
  }

  get passwordControl(): UntypedFormControl {
    return this.resetPasswordForm.get('password') as UntypedFormControl;
  }

  get arePasswordAndConfirmPasswordTouched(): boolean {
    return this.passwordControl.touched && this.confirmPasswordControl.touched;
  }

  get confirmPasswordControl(): UntypedFormControl {
    return this.resetPasswordForm.get('confirmPassword') as UntypedFormControl;
  }

  resetPassword(): any {
    if (!this.email || !this.resetToken) {
      return;
    }

    const { password } = this.resetPasswordForm.getRawValue();
    const userId = this.email.toLowerCase().trim();

    // restheart-accounts: reset via token (auto-login)
    this.authSrv
      .resetPasswordWithToken(userId, this.resetToken!, password)
      .pipe(
        tap(() => this.router.navigate(['', 'srvs'])),
        catchError((error: any) => {
          if (error.status === 400) {
            const dialogRef = this.dialog.open(WeakPasswordDialogComponent, { context: {} });
            return dialogRef.closed$.pipe(switchMap(() => EMPTY));
          } else {
            return errorHandlingFunction(this.dialog, ResetPasswordRequestErrorMessages)(error);
          }
        }),
        tap(() => toast.success('Your password is set'))
      )
      .subscribe();
  }

  private createResetPasswordForm(email: string, tokenOrOtp: string) {
    this.email = email;
    this.resetToken = tokenOrOtp;

    this.resetPasswordForm = this.fb.group(
      {
        password: ['', Validators.minLength(8)],
        confirmPassword: ['', Validators.minLength(8)],
      },
      {
        validators: this.equalPasswordValidatorFn,
      }
    );
  }

  private equalPasswordValidatorFn(control: AbstractControl): { [key: string]: any } | null {
    const password = control.get('password');
    const confirmPassword = control.get('confirmPassword');

    if (password && confirmPassword) {
      return password.value === confirmPassword.value ? null : { passwordMismatch: true };
    }

    return null;
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
